import z from 'zod'
import { RSS_DIRECTORY_PROVIDER_IDS } from '../helpers/rssDirectoryProvider'
import { layoutSchema } from './playlist'
import {
  rssLayoutIdSchema,
  rssLayoutOptionsSchema
} from './rssDirectoryRef'

export {
  rssDirectoryRefOverridesSchema,
  rssDirectoryRefSchema,
  rssLayoutIdSchema,
  rssLayoutOptionsSchema,
  type TRssDirectoryRef,
  type TRssDirectoryRefOverrides,
  type TRssLayoutId,
  type TRssLayoutOptions
} from './rssDirectoryRef'

export const rssDirectoryDefaultsSchema = z.object({
  durationSec: z.number().positive(),
  slideCount: z.number().int().min(1).max(20).optional(),
  slideDurationSec: z.number().int().min(3).max(120).optional(),
  maxAgeSec: z.number().int().min(0).optional()
})

export type TRssDirectoryDefaults = z.infer<typeof rssDirectoryDefaultsSchema>

/** Persisted in D1 / edited from admin (layout materialized at read time). */
export const rssDirectoryCatalogStoredEntrySchema = z.object({
  id: z.string().min(1).regex(/^[a-z0-9][a-z0-9-]*$/),
  label: z.string().min(1),
  category: z.string().min(1),
  provider: z.enum(RSS_DIRECTORY_PROVIDER_IDS),
  /** BCP 47 language tag for operator filtering (defaults to pt-BR when omitted on legacy rows). */
  language: z.string().min(2),
  /** ISO 3166-1 alpha-2 country code for operator filtering (defaults to BR when omitted). */
  country: z.string().length(2).regex(/^[A-Z]{2}$/),
  /** Canonical upstream RSS URL for any provider. */
  feedUrl: z.string().url(),
  layoutId: rssLayoutIdSchema,
  layoutOptions: rssLayoutOptionsSchema,
  defaults: rssDirectoryDefaultsSchema,
  enabled: z.boolean().default(true)
})

export type TRssDirectoryCatalogStoredEntry = z.infer<typeof rssDirectoryCatalogStoredEntrySchema>

export const rssDirectoryEntrySchema = rssDirectoryCatalogStoredEntrySchema.extend({
  layout: layoutSchema
})

export type TRssDirectoryEntry = z.infer<typeof rssDirectoryEntrySchema>

export const rssParsedItemSchema = z.object({
  title: z.string(),
  link: z.string().url(),
  pubDate: z.string().optional(),
  description: z.string().optional(),
  imageUrl: z.string().url().optional(),
  imageWidth: z.number().int().positive().optional(),
  imageHeight: z.number().int().positive().optional()
})

export type TRssParsedItem = z.infer<typeof rssParsedItemSchema>

/** Live feed stats for RSS directory picker tiles (feeds-api `/directory/freshness`). */
export const rssDirectoryListingFreshnessEntrySchema = z.object({
  id: z.string().min(1),
  itemCount: z.number().int().min(0).nullable(),
  latestItemPublishedAt: z.string().datetime().nullable()
})

export type TRssDirectoryListingFreshnessEntry = z.infer<typeof rssDirectoryListingFreshnessEntrySchema>

export const rssDirectoryListingFreshnessResponseSchema = z.object({
  fetchedAt: z.string().datetime(),
  entries: z.array(rssDirectoryListingFreshnessEntrySchema)
})

export type TRssDirectoryListingFreshnessResponse = z.infer<typeof rssDirectoryListingFreshnessResponseSchema>

/** Build G1 RSS URL from a path segment (admin helper / legacy migration only). */
export function buildG1RssFeedUrl(feedPath: string): string {
  const trimmed = feedPath.trim().replace(/^\/+|\/+$/g, '')
  return trimmed
    ? `https://g1.globo.com/rss/g1/${trimmed}/`
    : 'https://g1.globo.com/rss/g1/'
}
